/**
 * Defaults — SSOT de valores por defecto del juego lotowheel.
 *
 * Uso:
 *   - Frontend ESM: import { DEFAULT_CONFIG, DEFAULT_SPIN } from './defaults.js'
 *   - Backend CJS:  const { DEFAULT_CONFIG, DEFAULT_SPIN } = require('./defaults');
 */

import { WHEEL_CONFIG } from './wheel-config.js';
import { ROULETTE_TYPES } from './roulette-numbers-order-config.js';

// ═══════════════════════════════════════════════════════════════════════════
// MONEDA (importado desde system-defaults.js — NO es del juego, es del sistema)
// ═══════════════════════════════════════════════════════════════════════════

import { DEFAULT_MONEDA } from '../../system-defaults.js';
export { DEFAULT_MONEDA };

// ═══════════════════════════════════════════════════════════════════════════
// FICHAS
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_FICHAS = {
    denominacion: 100, // 100 centavos = 1 unidad de moneda (1 crédito = 1 sol)
    chips: [
        { valor: 1, label: '1', color: '#1976d2' },
        { valor: 2, label: '2', color: '#d32f2f' },
        { valor: 5, label: '5', color: '#388e3c' },
        { valor: 10, label: '10', color: '#7b1fa2' },
        { valor: 20, label: '20', color: '#212121' },
        { valor: 50, label: '50', color: '#ffc107' },
    ],
};

// ═══════════════════════════════════════════════════════════════════════════
// WALLET (Caja — denominaciones de depósito/retiro en efectivo)
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_WALLET = {
    denominaciones: [1, 5, 10, 20, 50, 100], // Billetes/monedas para caja (en unidades de moneda)
    modoEntrada: 'denominacion', // 'denominacion' | 'numpad'
};

// ═══════════════════════════════════════════════════════════════════════════
// LIMITES
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_LIMITES = {
    premioMaximo: 360000, // Premio máximo en centavos (3600 soles)
    plenoMultiplier: 36, // Multiplicador pago pleno (x36) — fallback cuando apuesta no trae multiplicador
    // Monto máximo de UNA apuesta (centavos). Tope por jugada individual.
    // 50000 = S/ 500 — un cajero promedio no vende apuestas por encima sin autorización; el admin sube el techo por operador.
    maxApuesta: 50000,
    // Monto máximo SUMADO del ticket (centavos). Suma de todas las apuestas antes de drawCount.
    // 500000 = S/ 5000 — evita tickets gigantes por error de tecla.
    maxTotalTicket: 500000,
    // Cantidad máxima de sorteos consecutivos que el cajero puede multiplicar en un ticket.
    // 10 es conservador para el default de venta; REGLAS_TICKET.drawCountMax=100 es el tope de schema DB, no del cajero.
    maxDrawCount: 10,
};

// ═══════════════════════════════════════════════════════════════════════════
// SPIN (Animacion de rueda)
// Re-exportado desde wheel-config.js para evitar duplicacion
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_SPIN = {
    duration: WHEEL_CONFIG.spin.duration,
    fmQuesitos: WHEEL_CONFIG.spin.fmQuesitos,
    cruiseRPM: WHEEL_CONFIG.spin.cruiseRPM,
    phases: { ...WHEEL_CONFIG.spin.phases },
    // Campos adicionales que solo usa defaults.js (no estan en wheel-config)
    correctionDelay: 1000, // Pausa en ms entre fin de animación y corrección de posición final
    tensionFinal: 7, // Intensidad del freno final (1=suave, 20=agonía extrema) — el core lo recibe como opts.tension
};

// ═══════════════════════════════════════════════════════════════════════════
// EFECTOS VISUALES
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_EFFECTS = {
    start: 'orange',
    end: 'burst',
};

// ═══════════════════════════════════════════════════════════════════════════
// IDLE (Comportamiento en espera)
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_IDLE = {
    mode: 'all',
    timeout: 10,
};

// ═══════════════════════════════════════════════════════════════════════════
// POZO (importado desde system-defaults.js — NO es del juego, es del sistema)
// ═══════════════════════════════════════════════════════════════════════════

import { DEFAULT_POZO } from '../../system-defaults.js';
export { DEFAULT_POZO };

// ═══════════════════════════════════════════════════════════════════════════
// HISTORIAL
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_HISTORY = {
    resultLimit: 120, // Cantidad de resultados recientes a mostrar
};

// ═══════════════════════════════════════════════════════════════════════════
// VOLATILIDAD (Motor backend — parámetros del resolver)
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_VOLATILIDAD = {
    rtp_target: 0.85,
    umbral_soltar_ratio: 0.2,
    umbral_proteger_ratio: -0.04,
    piso_ratio: -0.2,
    capacidad_base_ratio: 0.2,
    volumen_ciclo_estimado_inicial: 500000,
    fondo_reserva_minimo: -500000,
    ventana_movil_sorteos: 500,
    crucero_ventana_sorteos: 5000,
    buffer_tamano_maximo: 100,
};

// ═══════════════════════════════════════════════════════════════════════════
// TIMING (Protocolo v5)
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_TIMING = {
    intervalo_segundos_sorteo: 60, // Segundos entre sorteos (ciclo completo)
    intervalo_minimo_segundos: 3, // Mínimo técnico (resolver + liquidación + broadcast)
    close_before: 5, // Segundos antes del sorteo para cerrar apuestas
    warning_time: 10, // Segundos antes del cierre para mostrar warning
    min_usable_time: 20, // Segundos minimos utiles para que el jugador apueste
    spin_time: 10, // Segundos que dura la animacion del sorteo (UI only)
    grace_seconds: 2, // Ventana de gracia servidor: absorbe latencia SSE→Cloudflare→cliente→POST
    bet_margin_ms: 3000, // Margen cliente para cerrar UI de apuestas antes que el servidor
};

// ═══════════════════════════════════════════════════════════════════════════
// CONFIG COMPLETA (merge de todos los defaults)
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_CONFIG = {
    // Tipo de rueda inicial para forms de admin UI cuando se crea un sorteo
    // nuevo. Derivado de las recetas — agregar una receta nueva en
    // recetas-agnosticas.js puede cambiarlo sin tocar este archivo. NO es un
    // default money-path: runtime/components fallan si no reciben tipo explicito.
    tipoRuleta: Object.keys(ROULETTE_TYPES)[0],
    tema: 'CASINO_ROYALE',
    sound_pack: 'DEFAULT',
    idioma: 'es',
    moneda: DEFAULT_MONEDA,
    fichas: DEFAULT_FICHAS,
    limites: DEFAULT_LIMITES,
    spin: DEFAULT_SPIN,
    effects: DEFAULT_EFFECTS,
    idle: DEFAULT_IDLE,
    pozo: DEFAULT_POZO,
    timing: DEFAULT_TIMING,
    apuestasHabilitadas: null, // null = todas, array = solo las indicadas
    // Shape multi-canal de las apuestas habilitadas. Default `null` = el sorteo
    // no distingue por canal de venta y se cae al `apuestasHabilitadas` plano
    // (forma legacy, vigente hoy en el 100% de los sorteos). Cuando un sorteo
    // declara este objeto, los consumidores leen su lista vía
    // `resolverApuestasHabilitadasParaCanalDeVenta(sorteo, canal)`
    // (ver `shared/config/games/lotowheel/canal-de-venta.js`).
    apuestasHabilitadasPorCanalDeVenta: null,
};

// CommonJS (Node.js backend). El frontend usa ESM nativo via `import { ... }`.
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        DEFAULT_MONEDA,
        DEFAULT_FICHAS,
        DEFAULT_WALLET,
        DEFAULT_LIMITES,
        DEFAULT_SPIN,
        DEFAULT_EFFECTS,
        DEFAULT_IDLE,
        DEFAULT_POZO,
        DEFAULT_HISTORY,
        DEFAULT_VOLATILIDAD,
        DEFAULT_TIMING,
        DEFAULT_CONFIG,
    };
}
